import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TUserProfileInfo } from "../components/Pricing/v1/types";
import { APP_THEME, downgradeMaxtoPro } from "../data/constants";

type TState = {
  authToken: string;
  billType: "monthly" | "yearly";
  upgradeStep: number;
  currentRanking: number;
  selectedProductId: string;
  upgradePrice: number;
  credits: number;
  couponValue: string;
  error: boolean;
  successMsg: boolean;
  estimateSuccess: boolean;

  //
  profileCount: number;
  profileAddonStep: number;
  currentAddonQuantity: number;
  downgradeScreen: number;

  deleteAllAIProfiles: boolean;
  checkedItemsStore: TUserProfileInfo[];
  userProfiles: TUserProfileInfo[] | [];

  deleteCSM: boolean;
  showDeleteProfiles: boolean;
  missingFeatures: string[];
  showMarketplaceProfiles: boolean;

  isUpgradeModalOpen: boolean;
  isIntervalModalOpen: boolean;
  isAddProfileModalOpen: boolean;
  isAddCropStraightenModalOpen: boolean;
  reviewTheme: "dark" | "light";

  showReferredPricing: boolean;
};

type TActions = {
  setAuthToken: (token: string) => void;
  setBillType: (billType: "monthly" | "yearly") => void;
  setUpgradeStep: (step: number) => void;
  setCurrentRanking: (ranking: number) => void;
  setSelectedProductId: (id: string) => void;
  setUpgradePrice: (price: number) => void;
  setCredits: (credits: number) => void;
  setIsIntervalModalOpen: (isOpen: boolean) => void;
  setCouponValue: (value: string) => void;
  setError: (value: boolean) => void;
  setSuccessMsg: (value: boolean) => void;

  setProfileCount: (count: number) => void;
  setProfileAddonStep: (step: number) => void;
  setCurrentAddonQuantity: (quantity: number) => void;
  setDowngradeScreen: (value: number) => void;
  setDeleteAllAIProfiles: (value: boolean) => void;
  setUserProfiles: (value: TUserProfileInfo[]) => void;
  setCheckedItemsStore: (value: TUserProfileInfo[]) => void;
  setDeleteCSM: (value: boolean) => void;
  setShowDeleteProfiles: (value: boolean) => void;
  setMissingFeatures: (value: string[]) => void;
  setShowMarketplaceProfiles: (value: boolean) => void;

  setUpgradeModalOpen: (isOpen: boolean) => void;
  setEstimateSuccess: (value: boolean) => void;
  setIsAddProfileModalOpen: (isOpen: boolean) => void;
  setIsAddCropStraightenModalOpen: (isOpen: boolean) => void;
  setReviewTheme: (theme: "light" | "dark") => void;

  setShowReferredPricing: (state: boolean) => void;
};

type TReviewStore = TState & TActions;

const useReviewStore = create<TReviewStore>()(
  devtools((set, get) => ({
    authToken: "",
    setAuthToken: (token) => set({ authToken: token }),

    billType: "yearly",
    setBillType: (billType) => set({ billType }),

    upgradeStep: 1,
    setUpgradeStep: (step) => set({ upgradeStep: step }),

    currentRanking: -1,
    setCurrentRanking: (ranking) => set({ currentRanking: ranking }),

    selectedProductId: "",
    setSelectedProductId: (id) => set({ selectedProductId: id }),

    upgradePrice: 0,
    setUpgradePrice: (price) => set({ upgradePrice: price }),

    credits: 0,
    setCredits: (credits) => set({ credits }),

    couponValue: "",
    setCouponValue: (value) => set({ couponValue: value }),

    error: false,
    setError: (value) => set({ error: value }),

    successMsg: false,
    setSuccessMsg: (value) => set({ successMsg: value }),

    estimateSuccess: false,
    setEstimateSuccess: (value) => set({ estimateSuccess: value }),

    isUpgradeModalOpen: false,
    setUpgradeModalOpen: (isOpen) => set({ isUpgradeModalOpen: isOpen }),

    isIntervalModalOpen: false,
    setIsIntervalModalOpen: (isOpen) => set({ isIntervalModalOpen: isOpen }),

    isAddProfileModalOpen: false,
    setIsAddProfileModalOpen: (isOpen) =>
      set({ isAddProfileModalOpen: isOpen }),

    profileCount: 1,
    setProfileCount: (count) => set({ profileCount: count }),

    profileAddonStep: 1,
    setProfileAddonStep: (step) => set({ profileAddonStep: step }),

    currentAddonQuantity: 0,
    setCurrentAddonQuantity: (quantity) =>
      set({ currentAddonQuantity: quantity }),

    isAddCropStraightenModalOpen: false,
    setIsAddCropStraightenModalOpen: (isOpen) =>
      set({ isAddCropStraightenModalOpen: isOpen }),

    downgradeScreen: 0,
    setDowngradeScreen: (value) => set({ downgradeScreen: value }),

    deleteAllAIProfiles: false,
    setDeleteAllAIProfiles: (value) => set({ deleteAllAIProfiles: value }),

    userProfiles: [],
    setUserProfiles: (value) => set({ userProfiles: value }),

    deleteCSM: false,
    setDeleteCSM: (value) => set({ deleteCSM: value }),

    showDeleteProfiles: false,
    missingFeatures: downgradeMaxtoPro,
    setShowDeleteProfiles: (value) => set({ showDeleteProfiles: value }),
    setMissingFeatures: (value) => set({ missingFeatures: value }),

    showMarketplaceProfiles: false,
    setShowMarketplaceProfiles: (value) =>
      set({ showMarketplaceProfiles: value }),

    checkedItemsStore: [],
    setCheckedItemsStore: (value) => set({ checkedItemsStore: value }),

    reviewTheme: APP_THEME.LIGHT,
    setReviewTheme: (theme) => set({ reviewTheme: theme }),

    showReferredPricing: false,
    setShowReferredPricing: (state) => set({ showReferredPricing: state }),
  })),
);

export default useReviewStore;
